import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { EventType } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { FluentProvider } from '@fluentui/react-components';
import { BrowserRouter } from 'react-router-dom';

import { createMsalInstance } from './msalInstance';
import { EnvironmentProvider, EnvironmentQueryScope } from './context/EnvironmentContext';
import { ConsoleConfigError, loadConsoleConfig } from './config/consoleConfig';
import { setSignInHelperLinks } from './config/signInHelperLinks';
import { BootFailure, BootSplash } from './components/BootSplash';
import ErrorBoundary from './components/ErrorBoundary';
import App from './App';
import { airbridgeTheme } from './theme/airbridgeTheme';
// FD-623: Inter is bundled with the console, so no font request leaves its own origin.
import '@fontsource-variable/inter';
import './index.css';

// FD-466 (FD-ADR-043) — the root theme is AirBridge, applied ONCE here.
//
// It replaces `rubixTheme`, the Rubix-orange overlay this file carried from
// FD-060 until the rebrand. The whole theme now lives in
// `./theme/airbridgeTheme`, derived from the token module rather than declared
// as forty inline hexes: the values are published in
// `docs/design/rubix-ui-tokens.json` and gated by
// `tests/unit/docs/UiTokens.Tests.ps1`, so a hex written here would be a second
// source the gate cannot see.
//
// This is a ROOT-level customisation and that is what makes it safe. FD-060's
// hover-flicker came from a provider DYNAMICALLY SCOPED inside an
// interaction-sensitive subtree and rebuilt on render; this one mounts once at
// app start with a module-constant theme and holds it for the process lifetime,
// so Griffel never re-injects.

// FD-421 — the QueryClient used to be constructed here, once, for the process.
// It now lives in `EnvironmentQueryScope`, which builds a fresh one per
// environment and remounts on `key={environmentId}`. A process-wide cache is
// correct for a console that serves one deployment and is a cross-environment
// data leak for one that serves several: the keys say `['migrations']`, not
// which Manager answered.
// FD-276 — mount a root and paint the boot splash IMMEDIATELY, before waiting
// on MSAL. Until now nothing rendered until `initialize()` and
// `handleRedirectPromise()` both resolved, so the operator got the raw
// index.html — a white page for the duration, and a PERMANENTLY white page on
// failure, with the only explanation in the console.
//
// One root, rendered into three times (splash → app, or splash → failure).
// Calling createRoot twice on the same element is what produces React's
// duplicate-root warning and two competing trees.
const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');
const root = createRoot(rootEl);
root.render(<BootSplash />);

// FD-420 — boot order is CONFIG → MSAL → app.
//
// Nothing about the console is compiled in any more: the clientId, the
// authority, the AdminAccess scope and the environment list all arrive from
// `/config.json` at runtime (FD-ADR-040 §7), so MSAL cannot be constructed until
// that file has been fetched and validated. Hence the chain rather than the
// module-load `new PublicClientApplication(msalConfig)` this replaced.
loadConsoleConfig()
  .then((cfg) => {
    // FD-592 — the sign-in helper's store links (optional; see consoleConfig).
    setSignInHelperLinks(cfg.signInHelper);
    const msalInstance = createMsalInstance(cfg);

    return msalInstance
      .initialize()
      .then(() => msalInstance.handleRedirectPromise())
      .then((response) => {
        if (response?.account) {
          msalInstance.setActiveAccount(response.account);
        } else {
          const cached = msalInstance.getAllAccounts();
          if (cached.length > 0 && !msalInstance.getActiveAccount()) {
            msalInstance.setActiveAccount(cached[0]);
          }
        }

        msalInstance.addEventCallback((event) => {
          if (
            event.eventType === EventType.LOGIN_SUCCESS &&
            event.payload &&
            'account' in event.payload &&
            event.payload.account
          ) {
            msalInstance.setActiveAccount(event.payload.account);
          }
        });

        root.render(
          <StrictMode>
            <MsalProvider instance={msalInstance}>
              <FluentProvider theme={airbridgeTheme}>
                {/* BrowserRouter is now OUTSIDE EnvironmentProvider, which reads
                    `?env=` through useSearchParams and so needs a router above
                    it. And the query cache is INSIDE, keyed on the environment —
                    the nesting is the design, not an ordering accident. */}
                <BrowserRouter>
                  <EnvironmentProvider config={cfg}>
                    <EnvironmentQueryScope>
                      <ErrorBoundary>
                        <App />
                      </ErrorBoundary>
                    </EnvironmentQueryScope>
                  </EnvironmentProvider>
                </BrowserRouter>
              </FluentProvider>
            </MsalProvider>
          </StrictMode>,
        );
      });
  })
  .catch((err) => {
    // Keep the console line — it is what a developer greps for — but the
    // OPERATOR gets a screen that names the likely cause. A silent console
    // error behind a white page is a support ticket that starts with
    // "the site is down".
    //
    // The two causes are told apart because their remedies share nothing: a
    // config failure is a deployment that shipped without its config.json, an
    // MSAL failure is the Entra app registration. Printing the app-registration
    // copy over a 404 on config.json would send an operator to the wrong portal.
    const isConfig = err instanceof ConsoleConfigError;
    console.error(isConfig ? 'Console configuration failed' : 'MSAL initialization failed', err);
    root.render(
      <StrictMode>
        <BootFailure error={err} cause={isConfig ? 'config' : 'auth'} />
      </StrictMode>,
    );
  });
